import { EntityType } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Spinner } from '@frontend/elements';
import { EquipmentStateBadge } from '@frontend/equipment/badge';
import { CommonMessage } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { StockExtendedClient } from '@frontend/stock/api';
import { StockExtended } from '@frontend/stock/types';

import { MultiEntityButton } from '../../../../components/multi-entity-button/multi-entity-button.component';
import { PlatformObjectBadge } from '../../../../modal/platform-object-badge/platform-object-badge.component';
import { useAppDispatch } from '../../../../redux/store';

export const LayoutTable = () => {
    const dispatch = useAppDispatch();
    const { currentAccount } = useAccountRepository();

    if (!currentAccount) {
        return <Spinner />;
    }

    return (
        <ReactiveObjectList<StockExtended>
            id='contents-table'
            useStaticListId
            key='contents-table'
            fetch={(arg) => StockExtendedClient.fetchStocksExtended({ ...arg, account_id: currentAccount })}
            dispatch={dispatch}
            type={EntityType.STOCK}
            columns={{
                columns: ['spot', 'slot', 'product', 'categories', 'sku', 'ean', 'equipment', 'equipment_state'],
                headerOverwrite: new Map([
                    ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                    ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                    ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                    ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                    ['ean', CommonMessage.OBJECTS.PRODUCT.EAN],
                    ['equipment', CommonMessage.OBJECTS.EQUIPMENT.DEFAULT],
                    ['equipment_state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
                ]),
                customCells: new Map([
                    [
                        'spot',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                {obj.row.original.spot ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.spot.entity_id}
                                        entityType={EntityType.SPOT}
                                        display={obj.row.original.spot.name}
                                        foreign={obj.row.original.spot.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                    />
                                ) : (
                                    <span className='text-foreground-muted'>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'slot',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                {obj.row.original.slot ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.slot.entity_id}
                                        entityType={EntityType.SLOT}
                                        display={obj.row.original.slot.name}
                                        parentIds={{
                                            spot_id: obj.row.original.slot.data.spot_id,
                                            module_id: obj.row.original.slot.data.module_id
                                        }}
                                        foreign={obj.row.original.slot.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                    />
                                ) : (
                                    <span className='text-foreground-muted'>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'product',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                {obj.row.original.product ? (
                                    <div className='flex gap-1'>
                                        <PlatformObjectBadge
                                            id={obj.row.original.product.entity_id}
                                            entityType={EntityType.PRODUCT}
                                            display={obj.row.original.product.name}
                                            foreign={obj.row.original.product.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                                        />
                                        {obj.row.original.quantity > 1 && (
                                            <div
                                                data-variant='Default'
                                                className='bg-border-default inline-flex min-h-7 min-w-7 items-center justify-center gap-3 rounded px-1'>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>x{obj.row.original.quantity}</div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <span className='text-foreground-muted'>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'categories',
                        (obj) => {
                            if (!obj.row.original.categories || obj.row.original.categories.length === 0) {
                                return (
                                    <td className={defaultCellClassName}>
                                        <span className='text-foreground-muted'>/</span>
                                    </td>
                                );
                            }

                            return (
                                <td
                                    key={obj.row.original.categories[0].entity_id}
                                    className={defaultCellClassName}>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={obj.row.original.categories[0].entity_id}
                                            entityType={EntityType.PRODUCT_CATEGORY}
                                            display={obj.row.original.categories[0].name}
                                            key={obj.row.original.categories[0].entity_id}
                                            foreign={obj.row.original.categories[0].foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.categories[0])}
                                        />
                                        {obj.row.original.categories.length > 1 && (
                                            <MultiEntityButton
                                                entities={obj.row.original.categories.slice(1)}
                                                render={(category) => (
                                                    <PlatformObjectBadge
                                                        id={category.entity_id}
                                                        entityType={EntityType.PRODUCT_CATEGORY}
                                                        display={category.name}
                                                        key={category.entity_id}
                                                        foreign={category.foreign}
                                                        accountId={accountIdFromResolvedEntity(category)}
                                                    />
                                                )}
                                            />
                                        )}
                                    </div>
                                </td>
                            );
                        }
                    ],
                    [
                        'equipment',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                {obj.row.original.equipment ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.equipment.entity_id}
                                        entityType={EntityType.EQUIPMENT}
                                        display={obj.row.original.equipment.name}
                                        foreign={obj.row.original.equipment.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.equipment)}
                                    />
                                ) : (
                                    <span className='text-foreground-muted'>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'equipment_state',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                {obj.row.original.equipment_state ? (
                                    <span className='text-foreground-default'>
                                        <EquipmentStateBadge state={obj.row.original.equipment_state} />
                                    </span>
                                ) : (
                                    <span className='text-foreground-muted'>/</span>
                                )}
                            </td>
                        )
                    ]
                ])
            }}
            activeFilter={{
                searchKeys: ['spot', 'module', 'product', 'ean', 'sku'],
                filterKeys: ['spot_id', 'module_id', 'product_id', 'equipment_state'],
                displayName: new Map([
                    ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                    ['module', CommonMessage.OBJECTS.MODULE.DEFAULT],
                    ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                    ['ean', CommonMessage.OBJECTS.PRODUCT.EAN],
                    ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                    ['equipment_state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
                ])
            }}
        />
    );
};
